import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";

type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  duration: number;
  isRemoving?: boolean;
}

let toasts: Toast[] = [];
let listeners: Array<(toasts: Toast[]) => void> = [];

const subscribe = (listener: (toasts: Toast[]) => void) => {
  listeners.push(listener);
  // Flush any active toasts that were queued before this listener subscribed
  const activeToasts = toasts.filter((t) => !t.isRemoving);
  if (activeToasts.length > 0) {
    listener(activeToasts);
  }
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
};

const notify = (toast: Omit<Toast, "id">) => {
  const id = crypto.randomUUID();
  const newToast = { ...toast, id };
  // Replace any existing toast so only one is shown at a time
  toasts = [newToast];
  const newToastsArray = [newToast];
  // flushSync forces React to commit this state update immediately,
  // before the auto-remove timer starts — preventing the toast from
  // being removed before it ever renders (React 19 batching issue)
  flushSync(() => {
    listeners.forEach((listener) => listener(newToastsArray));
  });

  // Auto-remove after duration
  setTimeout(() => {
    removeToast(id);
  }, toast.duration * 1000);
};

const removeToast = (id: string) => {
  toasts = toasts.map((t) => (t.id === id ? { ...t, isRemoving: true } : t));
  const toastsWithRemoving = [...toasts];
  listeners.forEach((listener) => listener(toastsWithRemoving));

  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);

    const filteredToasts = [...toasts];
    listeners.forEach((listener) => listener(filteredToasts));
  }, 300);
};

export const ToastContainer: React.FC = () => {
  const [currentToasts, setCurrentToasts] = useState<Toast[]>([]);

  useEffect(() => {
    return subscribe(setCurrentToasts);
  }, []);

  if (currentToasts.length === 0) return null;

  return (
    <div
      className="ant-notification ant-notification-topRight"
      style={{
        position: "fixed",
        top: "24px",
        right: "24px",
        zIndex: 1010,
        marginRight: 0,
      }}
    >
      {currentToasts.map((toast) => (
        <div
          key={toast.id}
          className="ant-notification-notice ant-notification-notice-closable"
          style={{
            marginBottom: toast.isRemoving ? 0 : "16px",
            padding: 0,
            overflow: "hidden",
            lineHeight: 1.5715,
            borderRadius: "2px",
            boxShadow:
              "0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08), 0 9px 28px 8px rgba(0,0,0,0.05)",
            animation: toast.isRemoving
              ? "antNotificationFadeOut 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86)"
              : "antNotificationFadeIn 0.24s cubic-bezier(0.645, 0.045, 0.355, 1)",
          }}
        >
          <div className="ant-notification-notice-content">
            <div
              className={`ant-notification-notice-with-icon`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                padding: "16px 24px",
                background: "#fff",
              }}
            >
              <span
                className={`ant-notification-notice-icon ant-notification-notice-icon-${toast.type}`}
                style={{
                  marginRight: "12px",
                  fontSize: "24px",
                  lineHeight: "24px",
                  color: getToastColor(toast.type),
                }}
              >
                {getAntdIcon(toast.type)}
              </span>
              <div style={{ flex: 1 }}>
                <div
                  className="ant-notification-notice-message"
                  style={{
                    marginBottom: toast.description ? "8px" : 0,
                    color: "rgba(0,0,0,0.85)",
                    fontSize: "16px",
                    lineHeight: "24px",
                  }}
                >
                  {toast.message}
                </div>
                {toast.description && (
                  <div
                    className="ant-notification-notice-description"
                    style={{
                      fontSize: "14px",
                      color: "rgba(0,0,0,0.65)",
                    }}
                  >
                    {toast.description}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="ant-notification-notice-close"
                onClick={() => removeToast(toast.id)}
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "22px",
                  color: "rgba(0,0,0,0.45)",
                  outline: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "14px",
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                <span className="ant-notification-close-x">
                  <span
                    role="img"
                    aria-label="close"
                    className="anticon anticon-close ant-notification-close-icon"
                  >
                    <svg
                      viewBox="64 64 896 896"
                      focusable="false"
                      data-icon="close"
                      width="1em"
                      height="1em"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
                    </svg>
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      ))}
      <style>
        {`
          @keyframes antNotificationFadeIn {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }

          @keyframes antNotificationFadeOut {
            0% {
              max-height: 150px;
              margin-bottom: 16px;
              opacity: 1;
            }
            100% {
              max-height: 0;
              margin-bottom: 0;
              padding-top: 0;
              padding-bottom: 0;
              opacity: 0;
            }
          }
        `}
      </style>
    </div>
  );
};

const getToastColor = (type: ToastType): string => {
  switch (type) {
    case "success":
      return "#52c41a";
    case "error":
      return "#ff4d4f";
    case "warning":
      return "#faad14";
    case "info":
      return "#1890ff";
  }
};

const getAntdIcon = (type: ToastType): JSX.Element => {
  switch (type) {
    case "success":
      return (
        <svg
          viewBox="64 64 896 896"
          focusable="false"
          data-icon="check-circle"
          width="1em"
          height="1em"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0051.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z"></path>
          <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path>
        </svg>
      );
    case "error":
      return (
        <svg
          viewBox="64 64 896 896"
          focusable="false"
          data-icon="close-circle"
          width="1em"
          height="1em"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M685.4 354.8c0-4.4-3.6-8-8-8l-66 .3L512 465.6l-99.3-118.4-66.1-.3c-4.4 0-8 3.5-8 8 0 1.9.7 3.7 1.9 5.2l130.1 155L340.5 670a8.32 8.32 0 00-1.9 5.2c0 4.4 3.6 8 8 8l66.1-.3L512 564.4l99.3 118.4 66 .3c4.4 0 8-3.5 8-8 0-1.9-.7-3.7-1.9-5.2L553.5 514l130.1-155c1.2-1.4 1.8-3.3 1.8-5.2z"></path>
          <path d="M512 65C264.6 65 64 265.6 64 513s200.6 448 448 448 448-200.6 448-448S759.4 65 512 65zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path>
        </svg>
      );
    case "warning":
      return (
        <svg
          viewBox="64 64 896 896"
          focusable="false"
          data-icon="exclamation-circle"
          width="1em"
          height="1em"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path>
          <path d="M464 688a48 48 0 1096 0 48 48 0 10-96 0zm24-112h48c4.4 0 8-3.6 8-8V296c0-4.4-3.6-8-8-8h-48c-4.4 0-8 3.6-8 8v272c0 4.4 3.6 8 8 8z"></path>
        </svg>
      );
    case "info":
      return (
        <svg
          viewBox="64 64 896 896"
          focusable="false"
          data-icon="info-circle"
          width="1em"
          height="1em"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path>
          <path d="M464 336a48 48 0 1096 0 48 48 0 10-96 0zm72 112h-48c-4.4 0-8 3.6-8 8v272c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V456c0-4.4-3.6-8-8-8z"></path>
        </svg>
      );
  }
};

// Export the toast API
export const toast = {
  success: (message: string, description?: string, duration: number = 1.2) => {
    notify({ type: "success", message, description, duration });
  },
  error: (message: string, description?: string, duration: number = 1.2) => {
    notify({ type: "error", message, description, duration });
  },
  info: (message: string, description?: string, duration: number = 1.2) => {
    notify({ type: "info", message, description, duration });
  },
  warning: (message: string, description?: string, duration: number = 1.2) => {
    notify({ type: "warning", message, description, duration });
  },
};

// Initialize toast container in the DOM
let toastRoot: ReturnType<typeof createRoot> | null = null;

export const initToastContainer = () => {
  if (toastRoot) return;

  const container = document.createElement("div");
  container.id = "toast-container";
  document.body.appendChild(container);
  toastRoot = createRoot(container);
  toastRoot.render(<ToastContainer />);
};
